export interface FooterLink {
  label: string;
  url: string;
}

export interface FooterMenuColumn {
  title: string;
  links: FooterLink[];
}

export type FooterSocialPlatform =
  | 'instagram'
  | 'facebook'
  | 'twitter'
  | 'tiktok'
  | 'youtube'
  | 'linkedin'
  | 'pinterest'
  | 'custom';

export interface FooterSocial {
  platform: FooterSocialPlatform;
  url: string;
  label?: string;
}

/** A single guarantee/trust chip in the footer trust row (Kaged-style). */
export interface FooterTrustBadge {
  /** Named lucide icon (shield/check/truck/refresh/star/leaf/award) or a raw
   *  emoji/char. Blank renders a default check mark. */
  icon?: string;
  label: string;
  sublabel?: string;
}

export interface FooterNewsletter {
  enabled?: boolean;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  buttonLabel?: string;
  successMessage?: string;
  /** Show an additional phone/SMS opt-in field beside the email input. */
  showPhone?: boolean;
  phonePlaceholder?: string;
}

/**
 * Theme tokens consumed by full-template layout footers. Each layout
 * supplies its own defaults — merchant can override any token via
 * `storefrontConfig.footerContent.theme.<key>` and the rest fall through
 * to the layout's preset.
 */
export interface FooterTheme {
  /** Footer background colour. */
  bgColor?: string;
  /** Primary text colour for headings + main copy. */
  textColor?: string;
  /** Muted text colour for secondary copy / links. */
  mutedTextColor?: string;
  /** Brand accent (e.g. gold / cyan / lime). */
  accentColor?: string;
  /** Lighter accent — used for hover state on links. */
  accentLightColor?: string;
  /** Hairline / divider colour. */
  borderColor?: string;
  /** Stronger border — copyright row separator. */
  borderActiveColor?: string;

  /** Brand wordmark typography (the big italic "Atelier" line). */
  brandFontFamily?: string;
  brandFontStyle?: 'normal' | 'italic' | string;
  brandTextTransform?: 'none' | 'uppercase' | 'lowercase' | string;
  brandLetterSpacing?: string;
  brandFontWeight?: string | number;

  /** Menu column heading typography. */
  headingFontFamily?: string;
  headingTextTransform?: 'none' | 'uppercase' | 'lowercase' | string;
  headingLetterSpacing?: string;
  headingFontWeight?: string | number;
  headingFontSize?: string;

  /** Body / link typography. */
  linkFontFamily?: string;
  linkTextTransform?: 'none' | 'uppercase' | 'lowercase' | string;
  linkLetterSpacing?: string;
  linkFontSize?: string;

  /** Show the dedicated subscribe band. Default depends on layout. */
  showNewsletterBand?: boolean;
  /** Show the social-icon row. Default true. */
  showSocials?: boolean;
  /** Editorial/airy footer: underlined inputs, text-link submit, more whitespace. */
  minimal?: boolean;
  /** Giant full-bleed wordmark band above the bottom bar (Kaged-style). */
  showWordmarkBand?: boolean;
  wordmarkText?: string;
  wordmarkLetterSpacing?: string;
  /** Weight for the giant wordmark — reads best thin (300) vs the heavy display weight. */
  wordmarkFontWeight?: string | number;
  /** Invert footer palette — swap background and text (light↔dark). */
  invert?: boolean;
}

export interface FooterContent {
  newsletter?: FooterNewsletter;
  brandBio?: string;
  brandTagline?: string;
  brandText?: string;
  brandLogoUrl?: string;
  /** Multi-line address (newlines preserved). Used by editorial layouts. */
  brandAddress?: string;
  /** Single-line phone / contact string. */
  brandPhone?: string;
  /** Region / origin mark — e.g. "Made in Malaysia". */
  brandRegion?: string;
  /** Editorial brand mark — e.g. "MMXXVI · NO. 1". Supports {year} token. */
  brandMark?: string;
  /** Right-side caption on the bottom row (next to copyright).
   *  e.g. "Shipping worldwide", "30-day guarantee", "Numbered editions". */
  bottomTagline?: string;
  menuColumns?: FooterMenuColumn[];
  socials?: FooterSocial[];
  /** Guarantee row shown above the footer columns. Empty/absent hides it. */
  trustBadges?: FooterTrustBadge[];
  bottomLinks?: FooterLink[];
  copyright?: string; // supports {year} and {storeName} tokens
  /** Theme tokens consumed by full-template footer chrome. */
  theme?: FooterTheme;
}

export interface FooterContentDefaults extends FooterContent {
  storeName?: string;
}

const sanitizeString = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return trimmed.length > 0 ? value : fallback;
};

const sanitizeLink = (link: any, fallback: FooterLink): FooterLink => ({
  label: sanitizeString(link?.label, fallback.label),
  url: sanitizeString(link?.url, fallback.url),
});

const sanitizeColumn = (col: any, fallback: FooterMenuColumn): FooterMenuColumn => {
  const incomingLinks = Array.isArray(col?.links) ? col.links : fallback.links;
  return {
    title: sanitizeString(col?.title, fallback.title),
    links: incomingLinks.map((link: any, idx: number) =>
      sanitizeLink(link, fallback.links[idx] || { label: `Link ${idx + 1}`, url: '#' })
    ),
  };
};

const sanitizeSocial = (s: any): FooterSocial | null => {
  if (!s || typeof s !== 'object') return null;
  const url = typeof s.url === 'string' ? s.url.trim() : '';
  if (!url) return null;
  const platform = (typeof s.platform === 'string' ? s.platform : 'custom') as FooterSocialPlatform;
  return { platform, url, label: typeof s.label === 'string' ? s.label : undefined };
};

const sanitizeTrustBadge = (b: any): FooterTrustBadge | null => {
  if (!b || typeof b !== 'object') return null;
  const label = typeof b.label === 'string' ? b.label.trim() : '';
  if (!label) return null;
  return {
    label,
    icon: typeof b.icon === 'string' && b.icon.trim().length > 0 ? b.icon.trim() : undefined,
    sublabel: typeof b.sublabel === 'string' && b.sublabel.trim().length > 0 ? b.sublabel : undefined,
  };
};

export const resolveFooterContent = (
  config: any,
  defaults: FooterContent
): FooterContent => {
  const raw: any = config && typeof config === 'object' ? config : {};

  const newsletter: FooterNewsletter = {
    enabled:
      typeof raw?.newsletter?.enabled === 'boolean'
        ? raw.newsletter.enabled
        : defaults.newsletter?.enabled ?? true,
    eyebrow: sanitizeString(raw?.newsletter?.eyebrow, defaults.newsletter?.eyebrow || ''),
    title: sanitizeString(raw?.newsletter?.title, defaults.newsletter?.title || ''),
    subtitle: sanitizeString(raw?.newsletter?.subtitle, defaults.newsletter?.subtitle || ''),
    placeholder: sanitizeString(raw?.newsletter?.placeholder, defaults.newsletter?.placeholder || 'Your email address'),
    buttonLabel: sanitizeString(raw?.newsletter?.buttonLabel, defaults.newsletter?.buttonLabel || 'Join'),
    successMessage: sanitizeString(
      raw?.newsletter?.successMessage,
      defaults.newsletter?.successMessage || 'Welcome to the community.'
    ),
    showPhone:
      typeof raw?.newsletter?.showPhone === 'boolean'
        ? raw.newsletter.showPhone
        : defaults.newsletter?.showPhone,
    phonePlaceholder: sanitizeString(
      raw?.newsletter?.phonePlaceholder,
      defaults.newsletter?.phonePlaceholder || 'Phone number (SMS)'
    ),
  } as FooterNewsletter;

  const defaultCols = defaults.menuColumns || [];
  const incomingCols = Array.isArray(raw?.menuColumns) && raw.menuColumns.length > 0
    ? raw.menuColumns
    : defaultCols;
  const menuColumns: FooterMenuColumn[] = incomingCols.map((col: any, idx: number) =>
    sanitizeColumn(col, defaultCols[idx] || { title: `COLUMN ${idx + 1}`, links: [{ label: 'Link', url: '#' }] })
  );

  const incomingSocials = Array.isArray(raw?.socials) ? raw.socials : null;
  const socials: FooterSocial[] = (
    incomingSocials
      ? incomingSocials.map(sanitizeSocial).filter((v: any): v is FooterSocial => !!v)
      : defaults.socials || []
  );

  const incomingTrust = Array.isArray(raw?.trustBadges) ? raw.trustBadges : null;
  const trustBadges: FooterTrustBadge[] = (
    incomingTrust
      ? incomingTrust.map(sanitizeTrustBadge).filter((v: any): v is FooterTrustBadge => !!v)
      : defaults.trustBadges || []
  );

  const incomingBottom = Array.isArray(raw?.bottomLinks) ? raw.bottomLinks : null;
  const bottomLinks: FooterLink[] = (
    incomingBottom
      ? incomingBottom.map((l: any, idx: number) =>
          sanitizeLink(l, (defaults.bottomLinks || [])[idx] || { label: 'Link', url: '#' })
        )
      : defaults.bottomLinks || []
  );

  const passThrough = (val: unknown): string | undefined => {
    if (typeof val !== 'string') return undefined;
    const trimmed = val.trim();
    return trimmed.length > 0 ? val : undefined;
  };

  const rawTheme: any = raw?.theme && typeof raw.theme === 'object' ? raw.theme : {};
  const themeDefaults = (defaults.theme || {}) as FooterTheme;
  const theme: FooterTheme = {
    bgColor: passThrough(rawTheme.bgColor) ?? themeDefaults.bgColor,
    textColor: passThrough(rawTheme.textColor) ?? themeDefaults.textColor,
    mutedTextColor: passThrough(rawTheme.mutedTextColor) ?? themeDefaults.mutedTextColor,
    accentColor: passThrough(rawTheme.accentColor) ?? themeDefaults.accentColor,
    accentLightColor: passThrough(rawTheme.accentLightColor) ?? themeDefaults.accentLightColor,
    borderColor: passThrough(rawTheme.borderColor) ?? themeDefaults.borderColor,
    borderActiveColor: passThrough(rawTheme.borderActiveColor) ?? themeDefaults.borderActiveColor,
    brandFontFamily: passThrough(rawTheme.brandFontFamily) ?? themeDefaults.brandFontFamily,
    brandFontStyle: passThrough(rawTheme.brandFontStyle) ?? themeDefaults.brandFontStyle,
    brandTextTransform: passThrough(rawTheme.brandTextTransform) ?? themeDefaults.brandTextTransform,
    brandLetterSpacing: passThrough(rawTheme.brandLetterSpacing) ?? themeDefaults.brandLetterSpacing,
    brandFontWeight:
      typeof rawTheme.brandFontWeight === 'string' || typeof rawTheme.brandFontWeight === 'number'
        ? rawTheme.brandFontWeight
        : themeDefaults.brandFontWeight,
    headingFontFamily: passThrough(rawTheme.headingFontFamily) ?? themeDefaults.headingFontFamily,
    headingTextTransform: passThrough(rawTheme.headingTextTransform) ?? themeDefaults.headingTextTransform,
    headingLetterSpacing: passThrough(rawTheme.headingLetterSpacing) ?? themeDefaults.headingLetterSpacing,
    headingFontWeight:
      typeof rawTheme.headingFontWeight === 'string' || typeof rawTheme.headingFontWeight === 'number'
        ? rawTheme.headingFontWeight
        : themeDefaults.headingFontWeight,
    headingFontSize: passThrough(rawTheme.headingFontSize) ?? themeDefaults.headingFontSize,
    linkFontFamily: passThrough(rawTheme.linkFontFamily) ?? themeDefaults.linkFontFamily,
    linkTextTransform: passThrough(rawTheme.linkTextTransform) ?? themeDefaults.linkTextTransform,
    linkLetterSpacing: passThrough(rawTheme.linkLetterSpacing) ?? themeDefaults.linkLetterSpacing,
    linkFontSize: passThrough(rawTheme.linkFontSize) ?? themeDefaults.linkFontSize,
    showNewsletterBand:
      typeof rawTheme.showNewsletterBand === 'boolean'
        ? rawTheme.showNewsletterBand
        : themeDefaults.showNewsletterBand,
    showSocials:
      typeof rawTheme.showSocials === 'boolean'
        ? rawTheme.showSocials
        : themeDefaults.showSocials,
    showWordmarkBand:
      typeof rawTheme.showWordmarkBand === 'boolean'
        ? rawTheme.showWordmarkBand
        : (themeDefaults as any).showWordmarkBand,
    wordmarkText: passThrough(rawTheme.wordmarkText) ?? (themeDefaults as any).wordmarkText,
    wordmarkLetterSpacing:
      passThrough(rawTheme.wordmarkLetterSpacing) ?? (themeDefaults as any).wordmarkLetterSpacing,
    wordmarkFontWeight:
      typeof rawTheme.wordmarkFontWeight === 'string' || typeof rawTheme.wordmarkFontWeight === 'number'
        ? rawTheme.wordmarkFontWeight
        : (themeDefaults as any).wordmarkFontWeight,
    minimal:
      typeof rawTheme.minimal === 'boolean' ? rawTheme.minimal : (themeDefaults as any).minimal,
    invert:
      typeof rawTheme.invert === 'boolean' ? rawTheme.invert : (themeDefaults as any).invert,
  } as any;

  return {
    newsletter,
    brandBio: sanitizeString(raw?.brandBio, defaults.brandBio || ''),
    brandTagline: sanitizeString(raw?.brandTagline, defaults.brandTagline || ''),
    brandText: sanitizeString(raw?.brandText, defaults.brandText || ''),
    brandLogoUrl: sanitizeString(raw?.brandLogoUrl, defaults.brandLogoUrl || ''),
    brandAddress: sanitizeString(raw?.brandAddress, defaults.brandAddress || ''),
    brandPhone: sanitizeString(raw?.brandPhone, defaults.brandPhone || ''),
    brandRegion: sanitizeString(raw?.brandRegion, defaults.brandRegion || ''),
    brandMark: sanitizeString(raw?.brandMark, defaults.brandMark || ''),
    bottomTagline: sanitizeString(raw?.bottomTagline, defaults.bottomTagline || ''),
    menuColumns,
    socials,
    trustBadges,
    bottomLinks,
    copyright: sanitizeString(raw?.copyright, defaults.copyright || '© {year} {storeName}. All rights reserved.'),
    theme,
  };
};

export const formatCopyright = (
  template: string | undefined,
  storeName: string,
  year: number = new Date().getFullYear()
): string => {
  const tpl = template && template.trim().length > 0
    ? template
    : '© {year} {storeName}. All rights reserved.';
  return tpl.replace(/\{year\}/gi, String(year)).replace(/\{storeName\}/gi, storeName);
};

export const pickFooterContent = (storefrontConfig: any): any => {
  if (!storefrontConfig || typeof storefrontConfig !== 'object') return null;
  return (
    storefrontConfig.footerContent ||
    storefrontConfig.storefrontConfig?.footerContent ||
    null
  );
};
